import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { ResponseList } from '@outlaw/core';
import * as FeedActions from './feed.actions';
import { FeedByCommunityTabService } from '../../services/util/feed-by-community-tab.service';
import { PostExtenderService } from '../../services/util/post-extender.service';
import { CommunityItem, CommunityTabType, FeedParams } from '../../interfaces';

@Injectable()
export class FeedEffects {
  private actions = inject(Actions);
  private feedByCommunityTabService = inject(FeedByCommunityTabService);
  private postExtenderService = inject(PostExtenderService);

  loadTabItems$ = createEffect(() =>
    this.actions.pipe(
      ofType(FeedActions.loadTabItems),
      switchMap(({ tabId, userId, fetchParams, isFirstLoad, skipLikes }) =>
        this.processFeedRequest(tabId, userId, fetchParams, isFirstLoad, skipLikes)
      )
    )
  );

  private processFeedRequest(
    tabId: CommunityTabType,
    userId: string,
    fetchParams: FeedParams,
    isFirstLoad: boolean,
    skipLikes?: boolean,
  ): Observable<any> {
    const feedRequest = this.feedByCommunityTabService.getFeedByCommunityTab(tabId);

    return feedRequest({
      ...fetchParams,
      id: userId,
    }).pipe(
      switchMap((response: ResponseList<CommunityItem>) =>
        this.handleFeedResponse(response, tabId, fetchParams, isFirstLoad, skipLikes)
      ),
      catchError(error => of(FeedActions.loadTabItemsFailure({ tabId, error })))
    );
  }

  private handleFeedResponse(
    response: ResponseList<CommunityItem>,
    tabId: CommunityTabType,
    fetchParams: FeedParams,
    isFirstLoad: boolean,
    skipLikes?: boolean,
  ): Observable<any> {
    if (response.list.length === 0) {
      return of(
        FeedActions.loadTabItemsSuccess({
          tabId,
          items: response.list,
          currentPage: fetchParams?.page || 1,
          hasMore: !!response.links?.next,
          isFirstLoad,
        })
      );
    }

    return this.postExtenderService.extendPosts(response.list, { skipLikes }).pipe(
      map(extendedItems =>
        FeedActions.loadTabItemsSuccess({
          tabId,
          items: extendedItems,
          currentPage: fetchParams?.page || 1,
          hasMore: !!response.links?.next,
          isFirstLoad,
        })
      )
    );
  }
}
